import { useMemo } from "react";
import { DataTable } from "../DataTable";
import { Badge, Group, MultiSelect } from "@mantine/core";
import {
  IconClock,
  IconClockCancel,
  IconEye,
  IconLogs,
  IconSend2,
} from "@tabler/icons-react";
import dayjs from "dayjs";
import { Link } from "react-router-dom";
import { openScheduleModal } from "./ScheduleModal";
import {
  useSendCampaign,
  useScheduleCampaign,
  useCancelScheduleCampaign,
  useDeleteCampaign,
} from "../../api/campaigns";
import { useDeleteConfirm } from "../../hooks/useDeleteConfirm";
import { useConfirm } from "../../hooks/useConfirm";
import { isContentEmpty } from "@congrs/utils";
import {
  DeleteAction,
  EditAction,
  TableAction,
  TableActions,
  actionsColumn,
} from "../TableActions";

const formatDate = (date) => {
  return date ? dayjs(date).format("DD.MM.YYYY. [at] HH:mm") : "-";
};

const CampaignsTable = ({
  eventId,
  records,
  fetching,
  page,
  recordsPerPage,
  totalRecords,
  onPageChange,
  statusFilter,
  handleStatusFilterChange,
  tagsFilter,
  handleTagsFilterChange,
  availableTags,
}) => {
  const { mutate: sendCampaign } = useSendCampaign({
    eventId,
  });
  const { mutate: scheduleCampaign } = useScheduleCampaign({
    eventId,
  });
  const { mutate: cancelSchedule } = useCancelScheduleCampaign({
    eventId,
  });
  const { mutate: deleteCampaign } = useDeleteCampaign({
    eventId,
  });

  const confirmDelete = useDeleteConfirm("campaign", deleteCampaign);
  const confirm = useConfirm();
  const handleDeleteCampaign = (id) => {
    confirmDelete(
      { id },
      {
        onSuccess: () => {
          if (page > 1 && records.length === 1) {
            onPageChange(page - 1);
          }
        },
      }
    );
  };

  const handleSendCampaign = (id) => {
    confirm({
      title: "Send Campaign Now?",
      message:
        "Are you sure you want to send this campaign immediately? This action cannot be undone.",
      onConfirm: () => sendCampaign({ id }),
    });
  };

  const handleScheduleCampaign = (id) => {
    openScheduleModal((scheduledDate) => {
      scheduleCampaign({
        id,
        scheduledAt: scheduledDate.toISOString(),
      });
    });
  };

  const handleCancelScheduleCampaign = (id) => {
    confirm({
      title: "Cancel scheduled campaign?",
      message:
        "Are you sure you want to cancel this scheduled campaign? It will be reset to draft.",
      cancelLabel: "No",
      onConfirm: () => cancelSchedule({ id }),
    });
  };

  const statusColors = {
    draft: "gray",
    scheduled: "yellow",
    sent: "green",
  };

  const renderActions = (row) => {
    if (row.status === "draft") {
      const isEmpty = isContentEmpty(row.contentHtml);
      return (
        <TableActions>
          <TableAction
            label={isEmpty ? "Add content before sending" : "Send Now"}
            icon={IconSend2}
            color="green"
            disabled={isEmpty}
            onClick={() => handleSendCampaign(row.id)}
          />
          <TableAction
            label={isEmpty ? "Add content before scheduling" : "Schedule"}
            icon={IconClock}
            color="#15AABF"
            disabled={isEmpty}
            onClick={() => handleScheduleCampaign(row.id)}
          />
          <EditAction to={`/app/campaigns/${row.id}/edit`} />
          <DeleteAction
            label="Delete"
            onDelete={() => handleDeleteCampaign(row.id)}
          />
        </TableActions>
      );
    }

    if (row.status === "scheduled") {
      return (
        <TableActions>
          <TableAction
            label="View"
            icon={IconEye}
            color="blue"
            component={Link}
            to={`/app/campaigns/${row.id}/view`}
          />
          <TableAction
            label="Cancel Schedule"
            icon={IconClockCancel}
            color="red"
            onClick={() => handleCancelScheduleCampaign(row.id)}
          />
        </TableActions>
      );
    }

    if (row.status === "sent") {
      return (
        <TableActions>
          <TableAction
            label="View"
            icon={IconEye}
            color="blue"
            component={Link}
            to={`/app/campaigns/${row.id}/view`}
          />
          <TableAction
            label="View Logs"
            icon={IconLogs}
            color="orange"
            component={Link}
            to={`/app/campaigns/${row.id}/logs`}
          />
        </TableActions>
      );
    }

    return null;
  };

  const columns = useMemo(
    () => [
      {
        accessor: "subject",
        title: "Subject",
      },
      {
        accessor: "status",
        title: "Status",
        width: 300,
        filtering: statusFilter.length > 0,
        filter: (
          <MultiSelect
            label="status"
            placeholder="Filter by status"
            value={statusFilter}
            data={[
              { value: "draft", label: "Draft" },
              { value: "scheduled", label: "Scheduled" },
              { value: "sent", label: "Sent" },
            ]}
            onChange={(value) => {
              handleStatusFilterChange(value);
            }}
            clearable
          />
        ),
        render: (row) => (
          <Group wrap="no-wrap" justify="space-between">
            <Badge variant="light" color={statusColors[row.status]}>
              {row.status}
            </Badge>
            {row.status !== "draft" &&
              (row.status === "scheduled"
                ? formatDate(row.scheduledAt)
                : formatDate(row.sentAt))}
          </Group>
        ),
      },
      {
        accessor: "targetedTagIds",
        title: "Audience",
        filtering: tagsFilter.length > 0,
        width: 400,
        filter: (
          <MultiSelect
            label="audience"
            placeholder="Filter by audience"
            value={tagsFilter}
            data={availableTags.map((tag) => ({
              value: String(tag.id),
              label: tag.title,
            }))}
            onChange={handleTagsFilterChange}
            clearable
          />
        ),
        render: (row) => {
          const tagIds = row?.targetedTagIds || [];
          if (tagIds.length === 0)
            return (
              <Badge variant="light" color="blue">
                All recipients
              </Badge>
            );
          return (
            <Group gap={4} wrap="wrap">
              {tagIds.map((tagId) => {
                const tag = availableTags.find(
                  (t) => String(t.id) === String(tagId)
                );
                return tag ? (
                  <Badge key={tagId} variant="light" color={tag.color}>
                    {tag.title}
                  </Badge>
                ) : null;
              })}
            </Group>
          );
        },
      },
      {
        accessor: "recipientCount",
        title: "Recipients",
      },
      actionsColumn(renderActions),
    ],
    [
      statusFilter,
      handleStatusFilterChange,
      tagsFilter,
      handleTagsFilterChange,
      availableTags,
      page,
      records,
    ]
  );

  return (
    <DataTable
      columns={columns}
      records={records}
      fetching={fetching}
      page={page}
      recordsPerPage={recordsPerPage}
      totalRecords={totalRecords}
      onPageChange={onPageChange}
      borderRadius="md"
      verticalSpacing="md"
    />
  );
};

export default CampaignsTable;
